import { WrenchIcon } from "@phosphor-icons/react";
import type { Variants } from "motion/react";
import { motion, useAnimation } from "motion/react";
import type { HTMLAttributes } from "react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { cn } from "@/lib/utils";

export interface AnimatedWrenchIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface AnimatedWrenchIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  autoAnimate?: boolean;
  isHovered?: boolean;
}

const ROTATING_VARIANTS: Variants = {
  normal: {
    rotate: 0,
  },
  animate: {
    rotate: [0, 15, -15, 0],
    transition: {
      repeat: Number.POSITIVE_INFINITY,
      repeatType: "loop" as const,
      duration: 1.2,
      ease: "easeInOut",
    },
  },
};

const AnimatedWrenchIcon = forwardRef<
  AnimatedWrenchIconHandle,
  AnimatedWrenchIconProps
>(
  (
    {
      onMouseEnter,
      onMouseLeave,
      className,
      size = 16,
      autoAnimate = false,
      isHovered,
      ...props
    },
    ref,
  ) => {
    const controls = useAnimation();
    const isControlledRef = useRef(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [internalHovered, setInternalHovered] = useState(false);

    // Use external isHovered prop if provided, otherwise use internal state
    const shouldAnimate = isHovered !== undefined ? isHovered : internalHovered;

    // Handle auto-animate with 3-second timeout
    useEffect(() => {
      if (isControlledRef.current) return;

      if (autoAnimate) {
        controls.start("animate");

        // Stop animation after 3 seconds
        timeoutRef.current = setTimeout(() => {
          controls.start("normal");
        }, 3000);
      } else {
        controls.start(shouldAnimate ? "animate" : "normal");
      }

      // Cleanup timeout on unmount or when autoAnimate changes
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      };
    }, [autoAnimate, controls, shouldAnimate]);

    useImperativeHandle(ref, () => {
      isControlledRef.current = true;

      return {
        startAnimation: () => controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        onMouseEnter?.(e);
        // Only use internal hover state if isHovered prop is not provided
        if (!isControlledRef.current && isHovered === undefined) {
          setInternalHovered(true);
        }
        // Restart animation on hover (clear timeout and animate continuously)
        if (!isControlledRef.current) {
          // Clear existing timeout
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          // Start animation (will continue until hover ends)
          controls.start("animate");
        }
      },
      [onMouseEnter, isHovered, controls],
    );

    const handleMouseLeave = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        onMouseLeave?.(e);
        // Only use internal hover state if isHovered prop is not provided
        if (!isControlledRef.current && isHovered === undefined) {
          setInternalHovered(false);
        }
        // Stop animation when hover ends
        if (!isControlledRef.current) {
          controls.start("normal");
        }
      },
      [onMouseLeave, isHovered, controls],
    );

    return (
      <div
        className={cn("inline-flex items-center justify-center", className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        <motion.div
          animate={controls}
          variants={ROTATING_VARIANTS}
          style={{ originX: "50%", originY: "50%", display: "inline-flex" }}
        >
          <WrenchIcon size={size} weight="regular" />
        </motion.div>
      </div>
    );
  },
);

AnimatedWrenchIcon.displayName = "AnimatedWrenchIcon";

export { AnimatedWrenchIcon };
